import React, { useRef, useState } from 'react';
import api from '../../services/api';
import { formatFileSize } from '../../utils/formatters';
import { formatInUserTimezone } from '../../utils/timezone';

function DocumentList({ documents = [], leadId, onDelete, onChanged }) {
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');
  const replaceInputRefs = useRef({});

  if (!Array.isArray(documents) || documents.length === 0) {
    return <p className="text-muted">No documents attached</p>;
  }

  const download = async (documentId, fileName = 'Document') => {
    setBusyId(`download-${documentId}`);
    setError('');
    try {
      const data = await api.downloadDocument(leadId, documentId);
      if (!data?.blob) throw new Error('Document download returned no file');
      const objectUrl = window.URL.createObjectURL(data.blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to download document');
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (documentId, fileName) => {
    if (!window.confirm(`Remove "${fileName || 'this document'}"?`)) return;
    setBusyId(`delete-${documentId}`);
    setError('');
    try {
      await api.deleteDocument(leadId, documentId);
      if (onDelete) onDelete(documentId);
      if (onChanged) await onChanged();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to remove document');
    } finally {
      setBusyId(null);
    }
  };

  const replace = async (documentId, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setError('Each document must be 10MB or smaller');
      return;
    }
    setBusyId(`replace-${documentId}`);
    setError('');
    try {
      const formData = new FormData();
      formData.append('document', file);
      const result = await api.replaceDocument(leadId, documentId, formData);
      if (onChanged) await onChanged(result?.document);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to replace document');
    } finally {
      setBusyId(null);
      if (replaceInputRefs.current[documentId]) replaceInputRefs.current[documentId].value = '';
    }
  };

  return (
    <div className="document-list">
      {error && <div className="error-message" style={{ marginBottom: '12px' }}>{error}</div>}
      {documents.map((doc) => {
        const id = doc.document_id || doc.id;
        const fileName = doc.file_name || doc.fileName || 'Document';
        const uploadedAt = doc.uploaded_at || doc.created_at;
        return (
          <div className="document-item" key={id}>
            <div className="document-item-info">
              <i className={`fas fa-file-${getFileIcon(fileName)}`}></i>
              <button type="button" className="document-name document-download-link" onClick={() => download(id, fileName)} disabled={busyId !== null} title="Download document">
                {fileName}
              </button>
              <span className="document-size">{formatFileSize(doc.file_size || doc.fileSize || 0)}</span>
              {doc.document_type && <span className="document-type">{doc.document_type}</span>}
              {uploadedAt && <span className="document-uploaded-date">{formatInUserTimezone(uploadedAt)}</span>}
            </div>
            <div className="document-item-actions">
              <button type="button" className="btn btn-sm btn-primary" onClick={() => download(id, fileName)} disabled={busyId !== null}>
                <i className={`fas ${busyId === `download-${id}` ? 'fa-spinner fa-spin' : 'fa-download'}`}></i> Download
              </button>
              <button type="button" className="btn btn-sm btn-secondary" onClick={() => replaceInputRefs.current[id]?.click()} disabled={busyId !== null}>
                <i className={`fas ${busyId === `replace-${id}` ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`}></i> Replace
              </button>
              <input ref={el => { replaceInputRefs.current[id] = el; }} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt" style={{ display: 'none' }} onChange={e => replace(id, e.target.files?.[0])} />
              <button type="button" className="btn btn-sm btn-danger" onClick={() => remove(id, fileName)} disabled={busyId !== null}>
                <i className={`fas ${busyId === `delete-${id}` ? 'fa-spinner fa-spin' : 'fa-trash'}`}></i> Remove
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function getFileIcon(filename) {
  const ext = String(filename).split('.').pop().toLowerCase();
  const icons = { pdf: 'pdf', doc: 'word', docx: 'word', xls: 'excel', xlsx: 'excel', jpg: 'image', jpeg: 'image', png: 'image', gif: 'image', txt: 'alt' };
  return icons[ext] || 'alt';
}

export default DocumentList;
